import React from 'react'
import { withStyles } from '@mui/styles'
import { Typography, Container, Box, CircularProgress } from '@mui/material'

import ArrowBackIos from '@mui/icons-material/ArrowBackIos'

import { pageDisplayerStyles } from './page-displayer.styles'

/**
 * @typedef {object} PageDisplayerProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {boolean} [loading=false] - Si es true, muestra un spinner de carga centrado en la pantalla.
 * @property {boolean | string} [error=false] - Si es truthy, muestra el mensaje de error centrado. Puede ser true o un string con el mensaje.
 * @property {function(): void} [handleGoBack] - Callback para el botón de "Volver". Si no se proporciona, el botón no se muestra.
 * @property {string} [textGoBack='Back'] - Texto que se muestra en el botón de "Volver".
 * @property {React.ReactNode} children - El contenido principal de la página que se muestra cuando no hay loading ni error.
 */

/**
 * Un componente contenedor de página con estados de carga, error y navegación.
 *
 * Proporciona un layout estandarizado para páginas con:
 * - Estado de carga con spinner centrado
 * - Estado de error con mensaje
 * - Botón opcional de "Volver" con icono y texto personalizable
 * - Container responsive con ancho máximo y padding
 * - Fondo blanco para el contenido principal
 *
 * @component
 * @example
 * const MyDetailPage = () => {
 *   const [loading, setLoading] = useState(true);
 *   const [error, setError] = useState(false);
 *   const [data, setData] = useState(null);
 *   const navigate = useNavigate();
 *
 *   useEffect(() => {
 *     fetchData()
 *       .then(setData)
 *       .catch(() => setError('Error al cargar los datos'))
 *       .finally(() => setLoading(false));
 *   }, []);
 *
 *   return (
 *     <PageDisplayer
 *       loading={loading}
 *       error={error}
 *       handleGoBack={() => navigate(-1)}
 *       textGoBack="Volver al listado"
 *     >
 *       <Typography variant="h4">Detalles</Typography>
 *       <Box mt={2}>
 *         {data && <DetailContent data={data} />}
 *       </Box>
 *     </PageDisplayer>
 *   );
 * };
 *
 * @param {PageDisplayerProps} props - Las props del componente PageDisplayer.
 */
export const PageDisplayer = withStyles(pageDisplayerStyles)(({
  loading = false,
  error = false,
  handleGoBack,
  textGoBack = 'Back',
  classes,
  children,
}) => {
  if (loading) {
    return (
      <Box display="flex" height="100vh" justifyContent="center" alignItems="center">
        <CircularProgress size={40} />
      </Box>
    )
  }
  if (error) {
    return (
      <Box display="flex" height="100%" justifyContent="center" alignItems="center">
        <p>{error}</p>
      </Box>
    )
  }
  return (
    <Container className={classes.root} maxWidth="lg">
      <Box height="100%" mt={[2, 4]} mb={[2, 4]}>
        {handleGoBack ? (
          <div className={classes.return} onClick={handleGoBack}>
            <ArrowBackIos fontSize="inherit" />
            <Typography variant="body1">{textGoBack}</Typography>
          </div>
        ) : null}
        <Box bgcolor="common.white" p={['15px', '20px 40px']}>
          {children}
        </Box>
      </Box>
    </Container>
  )
})

export default PageDisplayer
